'use client'

/**
 * HeaderIndices — the global "market at a glance" strip in the top nav: the four US index ETFs
 * (S&P 500 / Nasdaq 100 / Dow / Russell 2000) with today's % change, color-coded. Replaces the old
 * single $SPY ticker so the whole market read lives in the header (and the Stock Market Today page can
 * drop its own MARKETS bar). Responsive: S&P always shows, the rest reveal at wider breakpoints. Polls
 * /api/market/indices (30s-cached) every 45s. Each links to that index's chart.
 */
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { sharedFetch } from '@/lib/sharedFetch'

type IndexQuote = { symbol: string; label: string; price: number; changePct: number }

// Compact codes for the header + the breakpoint at which each becomes visible (S&P always on).
const META: Record<string, { short: string; vis: string }> = {
  SPY: { short: 'S&P', vis: 'flex' },
  QQQ: { short: 'NDQ', vis: 'hidden xl:flex' },
  DIA: { short: 'DOW', vis: 'hidden xl:flex' },
  IWM: { short: 'RUT', vis: 'hidden 2xl:flex' },
}

export default function HeaderIndices({ variant = 'inline', compact = false }: { variant?: 'inline' | 'ribbon'; compact?: boolean } = {}) {
  const [ix, setIx] = useState<IndexQuote[] | null>(null)

  useEffect(() => {
    let alive = true
    // ⚠ THIS COMPONENT IS MOUNTED TWICE (Navbar renders a desktop and a mobile copy), and the hidden
    // one still runs its effects — `hidden lg:block` is CSS, it does not stop a fetch. Both copies
    // therefore hit /api/market/indices at the same instant on every page load. sharedFetch collapses
    // them onto ONE request; the 30s TTL also means the two polling timers cannot double up later.
    const load = async () => {
      try {
        const j = await sharedFetch<{ indices?: IndexQuote[] }>('/api/market/indices', 30_000)
        if (alive && Array.isArray(j.indices) && j.indices.length) setIx(j.indices)
      } catch { /* keep last */ }
    }
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, 45_000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  if (!ix?.length) return null

  // Ribbon: every index in a horizontally-scrollable strip (ignores the responsive vis classes), for
  // the mobile drawer where the top bar can no longer carry a market chip without crowding.
  if (variant === 'ribbon') {
    return (
      <div className="flex items-center gap-4 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {ix.map((q) => {
          const m = META[q.symbol] || { short: q.symbol, vis: '' }
          const up = q.changePct >= 0
          return (
            <Link
              key={q.symbol}
              href={`/chart/${q.symbol}`}
              prefetch={false}
              className="flex shrink-0 items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{m.short}</span>
              <span className={`text-[11px] font-bold tabular-nums ${up ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>{up ? '+' : ''}{q.changePct.toFixed(2)}%</span>
            </Link>
          )
        })}
      </div>
    )
  }

  // Compact (middle-ground logged-out header): one calm live signal — S&P only, with a live pulse dot —
  // instead of the full index strip, so it does not compete with the primary signup CTA.
  const shown = compact ? ix.filter((q) => q.symbol === 'SPY') : ix
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/60 px-2 py-1">
      {compact && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.18)]" />}
      {shown.map((q, i) => {
        const m = META[q.symbol] || { short: q.symbol, vis: 'hidden 2xl:flex' }
        const up = q.changePct >= 0
        return (
          <Link
            key={q.symbol}
            href={`/chart/${q.symbol}`}
            prefetch={false}
            title={`${q.label} (${q.symbol}) $${q.price.toFixed(2)} ${up ? '+' : ''}${q.changePct.toFixed(2)}% today`}
            className={`${compact ? 'flex' : m.vis} items-center gap-1 px-2 transition-opacity hover:opacity-80 ${i > 0 ? 'border-l border-border/60' : ''}`}
          >
            <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">{m.short}</span>
            <span className={`text-[11px] font-bold tabular-nums ${up ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>{up ? '+' : ''}{q.changePct.toFixed(2)}%</span>
          </Link>
        )
      })}
    </div>
  )
}
